function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import * as React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Loader } from '../Loader';
import { Popover } from '../Popover';
import { Tooltip } from '../Tooltip';
import styles from '@dtsl/css-button/dist/index.module.css';
import ChevronDown from '@dtsl/icons/dist/icons/react/ChevronDown';
import ChevronUp from '@dtsl/icons/dist/icons/react/ChevronUp';

// eslint-disable-next-line react/display-name
const Button = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    variant,
    label,
    icon,
    size,
    endIcon,
    loading,
    iconAlone,
    disabled,
    className,
    tooltip,
    ellipsis,
    tooltipValue,
    dropdownOpen,
    showArrowIcon,
    fullWidth,
    isSelected,
    labelClassName,
    hasPopover,
    popoverData,
    ariaLabel = 'button',
    isFloatingComp = false,
    dataTestId,
    ...props
  } = _ref;
  let btnVariant;
  const variantOfButton = variant === 'default' ? 'primary' : variant;
  if (iconAlone && variant === 'default') {
    btnVariant = `${styles['sib-btn_icon-stand-alone']} ${dropdownOpen ? styles['dropdown-open'] : ''}`;
  } else {
    btnVariant = `${styles[`sib-btn_variant--${variantOfButton}`]} ${dropdownOpen ? styles['dropdown-open'] : ''}`;
  }
  const btnClass = classNames({
    [styles['sib-btn']]: true,
    [styles['sib-btn--loader']]: loading,
    [styles['sib-btn--full-width']]: fullWidth,
    [styles['sib-btn--selected']]: isSelected,
    [btnVariant]: !!btnVariant,
    [styles[`sib-btn_size--${size}`]]: !!size,
    [styles['sib-btn_icon-alone']]: !!iconAlone,
    [className]: !!className
  });
  const dropdownIcon = dropdownOpen ? /*#__PURE__*/React.createElement(ChevronUp, {
    className: styles['icon-right']
  }) : /*#__PURE__*/React.createElement(ChevronDown, {
    className: styles['icon-right']
  });
  const buttonHTML = /*#__PURE__*/React.createElement("button", _extends({
    ref: ref,
    type: "button",
    className: btnClass,
    "data-testid": dataTestId,
    "aria-label": iconAlone || !/[a-zA-Z0-9]/.test(label) ? ariaLabel : null // check if label is not alpha numeric then show aria-label
    ,

    disabled: disabled || loading,
    "aria-disabled": disabled ? disabled : null
  }, props), icon ? icon : null, loading ? /*#__PURE__*/React.createElement(Loader, {
    isAbsolute: true,
    className: styles['sib-loader-alias']
  }) : null, !iconAlone ? ellipsis ? /*#__PURE__*/React.createElement("span", {
    className: ellipsis ? styles['ellipsis'] : ''
  }, label) : /*#__PURE__*/React.createElement("span", {
    className: `${styles['sib-btn--label']} ${labelClassName ? labelClassName : ''}`
  }, label) : null, showArrowIcon ? dropdownIcon : null, endIcon && !showArrowIcon && !iconAlone ? endIcon : null);
  if (tooltip) {
    if (isFloatingComp) {
      return /*#__PURE__*/React.createElement("div", {
        ref: ref
      }, /*#__PURE__*/React.createElement(Tooltip, tooltipValue, buttonHTML));
    } else {
      return /*#__PURE__*/React.createElement(Tooltip, tooltipValue, buttonHTML);
    }
  } else if (iconAlone && hasPopover) {
    return /*#__PURE__*/React.createElement(Popover, popoverData, buttonHTML);
  }
  return buttonHTML;
});
Button.propTypes = {
  size: PropTypes.oneOf(['', 'medium', 'small']),
  variant: PropTypes.oneOf(['default', 'primary', 'secondary', 'tertiary', 'destructive', 'destructive-secondary', 'destructive-tertiary', 'upgrade', 'upgrade-secondary', 'upgrade-tertiary', 'addon', 'addon-error']),
  dataTestId: PropTypes.string,
  label: PropTypes.string,
  labelClassName: PropTypes.string,
  iconAlone: PropTypes.bool,
  icon: PropTypes.node,
  endIcon: PropTypes.node,
  ellipsis: PropTypes.bool,
  disabled: PropTypes.bool,
  tooltip: PropTypes.bool,
  tooltipValue: PropTypes.object,
  hasPopover: PropTypes.bool,
  popoverData: PropTypes.object,
  dropdownOpen: PropTypes.bool,
  showArrowIcon: PropTypes.bool,
  className: PropTypes.string,
  loading: PropTypes.bool,
  fullWidth: PropTypes.bool,
  isSelected: PropTypes.bool,
  isFloatingComp: PropTypes.bool,
  ariaLabel: function (props, propName) {
    if (props['iconAlone'] && typeof props[propName] !== 'string') {
      return new Error(`prop ${propName} type of string must be supplied when using iconAlone button.`);
    }
    if (props['iconAlone'] && !props[propName]) {
      return new Error(`prop ${propName} must be supplied of type string when using iconAlone variant of button component.`);
    }
  }
};
Button.defaultProps = {
  variant: 'default',
  label: '',
  dataTestId: 'sib-button-testId',
  labelClassName: '',
  size: 'medium',
  ellipsis: false,
  disabled: false,
  iconAlone: false,
  tooltip: false,
  tooltipValue: {},
  hasPopover: false,
  popoverData: {},
  dropdownOpen: false,
  showArrowIcon: false,
  loading: false,
  fullWidth: false,
  isSelected: false,
  isFloatingComp: false
};
export default Button;